import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';

interface AggregateNodeConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
    inputColumns: any[];
}

export const AggregateNodeConfig: React.FC<AggregateNodeConfigProps> = ({ data, onChange, inputColumns }) => {
    const groups = data.groups || [];
    const aggregates = data.aggregates || [];

    // Memoize column options to avoid re-rendering issues
    const columnOptions = useMemo(() => {
        return inputColumns.map(c => (
            <option key={c.name} value={c.name}>{c.name}</option>
        ));
    }, [inputColumns]);

    const handleGroupChange = (colName: string, checked: boolean) => {
        const newGroups = checked
            ? [...groups, colName]
            : groups.filter((g: any) => g !== colName);
        onChange('groups', newGroups);
    };

    const addAggregation = () => {
        const newAggs = [...aggregates, { column: '', func: 'SUM', alias: '' }];
        onChange('aggregates', newAggs);
    };

    const removeAggregation = (idx: number) => {
        const newAggs = [...aggregates];
        newAggs.splice(idx, 1);
        onChange('aggregates', newAggs);
    };

    const updateAggregation = (idx: number, field: string, value: any) => {
        const newAggs = [...aggregates];
        const agg = { ...newAggs[idx], [field]: value };

        // Auto-alias logic
        if (field === 'column') {
            if (!agg.alias && value) agg.alias = `${agg.func}_${value}`;
        } else if (field === 'func') {
            if (agg.alias === `${newAggs[idx].func}_${agg.column}` && agg.column) {
                agg.alias = `${value}_${agg.column}`;
            }
        }

        newAggs[idx] = agg;
        onChange('aggregates', newAggs);
    };

    return (
        <div className="space-y-6">
            {/* Group By Selection */}
            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Group By Fields</label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    {inputColumns.length === 0 ? (
                        <div className="text-xs text-gray-400 italic p-2">No input columns available.</div>
                    ) : (
                        inputColumns.map((col) => {
                            const isChecked = groups.includes(col.name);
                            return (
                                <div key={col.name} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        checked={isChecked}
                                        onChange={(e) => handleGroupChange(col.name, e.target.checked)}
                                    />
                                    <span className="text-[11px] text-gray-700 truncate" title={col.name}>{col.name}</span>
                                    <span className="text-[9px] text-gray-400 uppercase">({col.type})</span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Aggregations List */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Aggregations</label>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] py-0" onClick={addAggregation}>
                        <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                </div>

                <div className="space-y-2">
                    {aggregates.map((agg: any, idx: number) => (
                        <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-200 space-y-2 relative group">
                            <div className="flex gap-2">
                                <select
                                    className="w-1/2 text-[10px] p-1 border rounded bg-white"
                                    value={agg.column}
                                    onChange={(e) => updateAggregation(idx, 'column', e.target.value)}
                                >
                                    <option value="">Select Column...</option>
                                    {columnOptions}
                                </select>
                                <select
                                    className="w-1/2 text-[10px] p-1 border rounded bg-white font-mono"
                                    value={agg.func}
                                    onChange={(e) => updateAggregation(idx, 'func', e.target.value)}
                                >
                                    <option value="SUM">Sum</option>
                                    <option value="COUNT">Count</option>
                                    <option value="AVG">Average</option>
                                    <option value="MIN">Min</option>
                                    <option value="MAX">Max</option>
                                    <option value="COUNT_DISTINCT">Count Distinct</option>
                                    <option value="FIRST">First</option>
                                    <option value="LAST">Last</option>
                                    <option value="STRING_AGG">String Agg (CSV)</option>
                                    <option value="STDDEV">Std Dev</option>
                                </select>
                            </div>
                            <input
                                className="w-full text-[10px] p-1 border rounded bg-white placeholder:text-gray-300"
                                placeholder="Output Alias (Optional)"
                                value={agg.alias || ''}
                                onChange={(e) => updateAggregation(idx, 'alias', e.target.value)}
                            />
                            <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border border-gray-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 text-red-500"
                                onClick={() => removeAggregation(idx)}
                            >
                                <Trash2 className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                    {aggregates.length === 0 && (
                        <div className="text-center py-2 text-xs text-gray-400 italic border border-dashed rounded">
                            No aggregations defined.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
